import React, { Component, createRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import {
  Routes,
  PubSubEvents,
  GaAnalyticEvents,
  BookingFormOptions,
  SessionStorageKeys,
  AdobeOnClickEventId,
} from '../../constants';
import Events from '../../events';
import {
  getPracticeCloseHours,
  getTenantSecondaryColor,
  getPracticeFormattedPhoneNumber,
  getClassNameOrientation,
  replaceParameterInBookingUrl,
  checkIsEexpertBadge,
  overrideBookingUrl,
  formatUrlWithoutProtocol,
} from '../../utils/tenantUtils';
import { removeNullValues } from '../../utils/arrayUtils';
import {
  parseQueryStrings,
  stringifyQueryStrings,
} from '../../utils/urlParamUtils';
import DateTime from '../../dateTime';
import withRouter from '../../hoc/withRouter';
import MapInfoContent from './mapInfoContent';
import withConfig from '../../hoc/withConfig';
import { debounce } from '../../utils/debounceUtils';
import {
  getBaiduUserAddressByCoords,
  getUserAddressByCoords,
} from '../../utils/geocodeUtils';
import { generateRoute } from '../../utils/routeUtils';
import { trackEvent } from '../../utils/analyticUtils';
import { getGoogleMapStyling } from '../../utils/mapUtils';
import { isMobile } from '../../utils/domUtils';
import Session from '../../session';
import { adobeTrackCTAClick } from '../../utils/adobeAnalyticsUtils';
import { pluckBaiduCityState, pluckCityState } from '../../utils/searchUtils';
import APPLICATION_CONSTANTS from '../../applicationConstants';
/**
 * Represents the map component for showing practices.
 */
class Map extends Component {
  /**
   * Initializes a new instance of the Map component.
   * @param {Object} props The component properties.
   */
  constructor(props) {
    super(props);
    this.state = {
      markers: [],
      showUserLocationOnMap: false,
      selectedPractice: {},
    };
    this._map = null;
    this._mapRef = createRef();
    this._currMapBounds = null;
    this._mapInfoWindow = null;
    this._mapInfoContentCont = null;
    this._mapSearchAreaButton = null;
    this._onSafeMapDrag = debounce(() => this._onMapDragEnd(), 250);
    this._onSafeWindowResize = debounce(() => this._onWindowResize(), 250);
    this.use_mobile_view = this.props.config.use_mobile_view;
  }

  /**
   * Executes when the component has mounted to the DOM.
   */
  componentDidMount() {
    if (!google.maps) {
      throw new Error('Google Maps must be initialized first.');
    }

    window.addEventListener('resize', this._onSafeWindowResize);
    Events.on(PubSubEvents.practiceSelected, this._onSelectMarker);
    Events.on(PubSubEvents.showUserLocation, this._onShowUserLocation);
    Events.on(PubSubEvents.practiceUpdated, this._onPracticeUpdated);

    this._initMaps();
    this._onSafeWindowResize();
  }

  componentDidUpdate(prevProps) {
    const { showUserLocationOnMap, markers } = this.state;
    const { practices } = this.props;
    const allowLocationAccess = Session.getItem(
      SessionStorageKeys.allowLocationAccess
    );

    const practiceArrayCompare = (practices1, practices2) => {
      let isEqual = true;

      for (let index = 0; index < practices1?.length; ++index) {
        const practice = practices1[index];

        if (!practices2?.some((p) => practice.practiceId === p.practiceId)) {
          isEqual = false;
          break;
        }
      }

      return isEqual;
    };

    const isSameAddress = () => {
      const { search, hash } = this.props.location;
      const queryStringValues = parseQueryStrings(search, hash);

      const { search: prevSearch, hash: prevHash } = prevProps.location;
      const prevQueryStringValues = parseQueryStrings(prevSearch, prevHash);

      return queryStringValues.address === prevQueryStringValues.address;
    };

    const showUserAndMarkerCountEqualPractices =
      showUserLocationOnMap &&
      allowLocationAccess &&
      markers.length === practices.length;
    const prevPracticesNotSameWithCurrent = !Array.isEqual(
      prevProps.practices,
      practices,
      practiceArrayCompare
    );
    const currPracticesNotSameWithPrevious = !Array.isEqual(
      practices,
      prevProps.practices,
      practiceArrayCompare
    );
    const shouldRecreateMarkers =
      !isSameAddress() &&
      !showUserLocationOnMap &&
      !prevPracticesNotSameWithCurrent &&
      !currPracticesNotSameWithPrevious &&
      markers.length > practices.length;

    if (showUserAndMarkerCountEqualPractices) {
      const userMarker = this._createUserLocationMarker();
      const { search, hash } = this.props.location;
      const queryStringValues = parseQueryStrings(search, hash);
      this._currAddress = queryStringValues.address;

      this.setState((prev) => ({
        markers: [...prev.markers, userMarker],
      }));
    }

    // Recreate the markers everytime the practices change (new search).
    if (
      shouldRecreateMarkers ||
      prevPracticesNotSameWithCurrent ||
      currPracticesNotSameWithPrevious
    ) {
      const { search, hash } = this.props.location;
      const queryStringValues = parseQueryStrings(search, hash);

      this._currAddress = queryStringValues.address;
      this._clearPracticeMarkers();
      this._createPracticeMarkers();
    }
  }

  componentWillUnmount() {
    const { markers } = this.state;

    if (markers?.length > 0) {
      for (let index = 0; index < markers.length; ++index) {
        markers[index].setMap(null);
      }
    }

    window.removeEventListener('resize', this._onSafeWindowResize);

    Events.removeListener(
      PubSubEvents.showUserLocation,
      this._onShowUserLocation
    );

    Events.removeListener(PubSubEvents.practiceSelected, this._onSelectMarker);

    Events.removeListener(
      PubSubEvents.practiceUpdated,
      this._onPracticeUpdated
    );

    this._mapSearchAreaButton.removeEventListener(
      'click',
      this._onSearchAreaClick
    );
  }

  _onPracticeUpdated = (updatedPractice) => {
    if (this._mapInfoWindow && this._mapInfoWindow.getContent()) {
      const content = this._mapInfoWindow.getContent();
      const heartImg = content.querySelector('img[alt^="heart-"]');
      if (heartImg) {
        const heartImgUrl = `${
          window.locatorTenancy.locatorRootUri
        }/assets/images/heart-${
          updatedPractice.isFavorite ? 'filled' : 'unfilled'
        }.png`;
        heartImg.src = heartImgUrl;
        heartImg.alt = `heart-${
          updatedPractice.isFavorite ? 'filled' : 'unfilled'
        }`;
        heartImg.className = `map-info-content__practice-favorite${
          updatedPractice.isFavorite ? '--filled' : ''
        }`;
      }
    }
  };

  _onShowUserLocation = (boolVal) => {
    this.setState({ showUserLocationOnMap: boolVal });
  };

  _clearPracticeMarkers = () => {
    this.setState(() => {
      const { markers } = this.state;

      if (markers.length > 0) {
        for (let index = 0; index < markers.length; ++index) {
          markers[index].setMap(null);
        }
      }

      return {
        markers: [],
      };
    });
  };

  _createUserLocationMarker = () => {
    const { Marker } = google.maps;
    const { locatorRootUri } = window.locatorTenancy;
    const userCurrentLocation = Session.getItem(
      SessionStorageKeys.userCurrentLocation
    );

    const userLocationMarker = new Marker({
      map: this._map,
      title: 'Your location',
      position: {
        lat: parseFloat(userCurrentLocation.lat),
        lng: parseFloat(userCurrentLocation.long),
      },
      icon: {
        url: `${locatorRootUri}/assets/images/location-point.png`,
        scaledSize: {
          width: 120,
          height: 120,
        },
        anchor: {
          x: 60,
          y: 60,
        },
      },
      zIndex: 1,
    });

    return userLocationMarker;
  };

  _createPracticeMarkers = () => {
    const { practices } = this.props;

    if (this._map && practices.length > 0) {
      const { LatLngBounds } = google.maps;
      const tenentThemeColor = getTenantSecondaryColor();
      let pinImage = '';
      const icon = {
        fillColor: tenentThemeColor,
        strokeColor: tenentThemeColor,
        fillOpacity: 1.0,
        anchor: {
          x: 15,
          y: 45,
        },
      };
      const { config } = this.props;

      if (config.map_pin_image) {
        pinImage = `${window.locatorTenancy.locatorRootUri}${config.map_pin_image}`
      } else if (config.map_pin_icon) {
        icon.path = config.map_pin_icon;
        Object.assign(icon, config?.map_pin_icon_property);
      } else {
        icon.path =
          'M25.6054322,4.39453265 C22.6758402,1.46460449 19.1406961,0 15,0 C10.8593038,0 7.32415974,1.46496408 4.39456776,4.39453265 C1.46497578,7.32410123 0,10.8592171 0,14.9998801 C0,17.1290044 0.322193987,18.8769648 0.966581961,20.2441207 L11.6601141,42.9201394 C11.9531812,43.5645222 12.4073452,44.0722613 13.0226063,44.4429972 C13.6378674,44.8140927 14.2969986,45 15.0003596,45 C15.7033609,45 16.3628518,44.8140927 16.9781128,44.4429972 C17.5933739,44.0722613 18.0568873,43.5645222 18.3693724,42.9201394 L29.0330584,20.2441207 C29.6778059,18.8769648 29.9999999,17.1290044 29.9999999,14.9998801 C30.0003595,10.8592171 28.5353837,7.32446082 25.6054322,4.39453265 L25.6054322,4.39453265 Z M20.3025363,20.3027337 C18.8379201,21.7673382 17.0705278,22.4998202 15,22.4998202 C12.9294721,22.4998202 11.1620799,21.7673382 9.69710407,20.3027337 C8.23212828,18.8377696 7.49964039,17.0703915 7.49964039,14.9998801 C7.49964039,12.9297284 8.23212828,11.1619907 9.69710407,9.69738619 C11.1620799,8.23242211 12.9298317,7.49994007 15,7.49994007 C17.0705278,7.49994007 18.8379201,8.23242211 20.3025363,9.69738619 C21.7675121,11.1619907 22.5,12.9297284 22.5,14.9998801 C22.5,17.0700319 21.7675121,18.83741 20.3025363,20.3027337 L20.3025363,20.3027337 Z';
      }

      const { Marker } = google.maps;
      const markers = [];
      const mapBounds = new LatLngBounds();

      for (let index = 0; index < practices.length; ++index) {
        const practice = practices[index];
        const { practiceId } = practice;
        const { lat, lng } = practice.position;

        const marker = new Marker({
          map: this._map,
          icon: pinImage || icon,
          position: {
            lat: lat,
            lng: lng,
          },
          practiceId: practiceId,
        });

        marker.addListener('click', () => this._onMarkerClick(marker));
        mapBounds.extend(marker.position);
        markers.push(marker);
      }

      const { showUserLocationOnMap } = this.state;
      const allowLocationAccess = Session.getItem(
        SessionStorageKeys.allowLocationAccess
      );

      if (showUserLocationOnMap && allowLocationAccess) {
        const userLocationMarker = this._createUserLocationMarker();
        markers.push(userLocationMarker);
        this.setState(() => ({ showUserLocationOnMap: false }));
      }

      this._currMapBounds = mapBounds;
      this._map.fitBounds(mapBounds);
      this.setState(() => ({ markers: markers }));
    }
  };

  _createSearchAreaControl = () => {
    if (this._map) {
      if (!this._mapSearchAreaButton) {
        const { t } = this.props;
        this._mapSearchAreaButton = document.createElement('button');
        this._mapSearchAreaButton.textContent = t('text_search_this_area');
        this._mapSearchAreaButton.classList.add(
          this.use_mobile_view ? 'mobile__map__search-area' : 'map__search-area'
        );
        this._mapSearchAreaButton.addEventListener(
          'click',
          this._onSearchAreaClick
        );
      }

      this._map.controls[google.maps.ControlPosition.TOP_CENTER].push(
        this._mapSearchAreaButton
      );
    }
  };

  _initMaps = () => {
    try {
      const { searchLocationCoords } = this.props;

      if (searchLocationCoords) {
        const { lat, lng } = searchLocationCoords;

        window.googleMapsApiLoader
          .importLibrary('maps')
          .then(({ Map, InfoWindow }) => {
            this._map = new Map(this._mapRef.current, {
              center: {
                lat: parseFloat(lat),
                lng: parseFloat(lng),
              },
              zoom: 8,
              fullscreenControl: false,
              mapTypeControl: false,
              streetViewControl: false,
              styles: getGoogleMapStyling(this.props),
            });

            this._map.addListener('drag', this._onSafeMapDrag);
            this._map.addListener('bounds_changed', this._onMapBoundsChange);
            this._map.addListener('tilesloaded', this._onMapTilesLoaded);

            const offSetValueDesktop = new google.maps.Size(0, 240);
            const offSetValueMobile = new google.maps.Size(0, 400);

            this._mapInfoWindow = new InfoWindow({
              content: '',
              pixelOffset: isMobile() ? offSetValueMobile : offSetValueDesktop,
            });
            this._createPracticeMarkers();
            this._createSearchAreaControl();
          });
      }
    } catch (error) {
      console.error(error);
    }
  };

  _onBookNowClick = (practice, e) => {
    const { config } = this.props;
    const { appointmentUrl, bookingLinkBehavior, isWholesale } = practice;
    const OpenInIFrame =
      bookingLinkBehavior !== 'OpenInNewTab' &&
      bookingLinkBehavior !== 'NavigateTo';
    let updatedAppointmentUrl = null;

    config.enable_adobe_onclick_tracking && adobeTrackCTAClick(e);
    trackEvent(GaAnalyticEvents.map.bookAppointmentClick(practice));
    trackEvent(GaAnalyticEvents.map.bookAppointmentClickApi(practice));

    if (config.booking_url_subsitution_options) {
      updatedAppointmentUrl = replaceParameterInBookingUrl(
        config.booking_url_subsitution_options,
        appointmentUrl
      );
    }

    if (config?.override_tab_booking_url) {
      updatedAppointmentUrl = overrideBookingUrl(config.override_tab_booking_url, appointmentUrl);
    }

    if (OpenInIFrame) {
      Events.emit(PubSubEvents.bookingPracticeSelected, practice);
    } else {
      let target = bookingLinkBehavior === 'NavigateTo' ? '_self' : '_blank';

      if (isWholesale) {
        // Ignores bookingLinkBehavior and opens link in a new tab
        target = '_blank';
      }

      window.open(
        updatedAppointmentUrl ? updatedAppointmentUrl : formatUrlWithoutProtocol(appointmentUrl),
        target
      );
    }
  };

  _onCloseMapInfoContent = (marker) => {
    trackEvent(GaAnalyticEvents.map.closePinClick(this._currAddress));

    if (marker) {
      marker.icon = {
        ...marker.icon,
        scale: 1,
      };
      marker.setMap(null);
      marker.setMap(this._map);
    }

    this._mapInfoWindow.close();
  };

  _onMapTilesLoaded = () => {
    const appRoot = document.getElementById('locatorv2embed');
    const fcnName = appRoot?.getAttribute('onMapLoaded');
    if (fcnName !== null) {
      const callback = window[fcnName];
      if (typeof callback === 'function') {
        callback();
      }
    }
  };

  _onMapBoundsChange = () => {
    if (this._map) {
      const mapDiv = this._map.getDiv();

      if (
        mapDiv.firstChild.clientHeight === window.innerHeight &&
        mapDiv.firstChild.clientWidth === window.innerWidth
      ) {
        trackEvent(GaAnalyticEvents.map.fullscreenClick(this._currAddress));
      }
    }
  };

  _onMapDragEnd = () => {
    if (
      this._mapSearchAreaButton &&
      !this._mapSearchAreaButton.classList.contains(
        this.use_mobile_view
          ? 'mobile__map__search-area--visible'
          : 'map__search-area--visible'
      )
    ) {
      trackEvent(GaAnalyticEvents.map.scrollMapChange(this._currAddress));

      this._mapSearchAreaButton.classList.add(
        this.use_mobile_view
          ? 'mobile__map__search-area--visible'
          : 'map__search-area--visible'
      );
    }
  };

  _onMarkerClick = (marker) => {
    const { practices } = this.props;
    const practice = practices.find((p) => p.practiceId === marker.practiceId);

    this.setState(() => ({
      selectedPractice: practice,
    }));

    Events.emit(PubSubEvents.practiceSelected, practice);

    Events.emit(PubSubEvents.showResultsDrawer, false);

    this._mapSearchAreaButton.classList.remove(
      this.use_mobile_view
        ? 'mobile__map__search-area--visible'
        : 'map__search-area--visible'
    );
  };

  _onPhoneClick = (practice, e) => {
    const { config } = this.props;
    // config.enable_adobe_onclick_tracking && adobeTrackCTAClick(e);
    trackEvent(GaAnalyticEvents.map.callNowClick(practice));
    trackEvent(GaAnalyticEvents.map.callNowClickApi(practice));
  };

  _onSearchAreaClick = async () => {
    if (this.state.showUserLocationOnMap) {
      this.setState(() => ({ showUserLocationOnMap: false }));
    }

    try {
      const { use_baidu_map } = this.props.config;
      trackEvent(GaAnalyticEvents.map.searchThisAreaClick(this._currAddress));

      this._mapSearchAreaButton.classList.remove(
        this.use_mobile_view
          ? 'mobile__map__search-area--visible'
          : 'map__search-area--visible'
      );

      const mapCenter = this._map.center;
      const lat = parseFloat(mapCenter.lat());
      const lng = parseFloat(mapCenter.lng());
      let shortenedAddress = '';

      const coords = {
        latitude: lat,
        longitude: lng,
      };

      if (use_baidu_map) {
        const { addressComponent } = await getBaiduUserAddressByCoords(coords);
        shortenedAddress = pluckBaiduCityState(addressComponent);
      } else {
        const { components } = await getUserAddressByCoords(coords);
        shortenedAddress = pluckCityState(components);
      }

      const { search, hash } = this.props.location;
      const queryStringValues = parseQueryStrings(search, hash);

      queryStringValues.address = shortenedAddress;
      queryStringValues.lat = lat;
      queryStringValues.long = lng;
      queryStringValues.currentPage = 1;
      queryStringValues.isFirstLoad = this.props.isFirstLoad;

      const { config } = this.props;
      const queryStrings = stringifyQueryStrings(queryStringValues);
      const resultsRoute = generateRoute(config, Routes.results);
      const { navigate } = this.props;
      const url = window.location.hash.split('#')[1]?.startsWith('/result')
        ? `?${queryStrings}`
        : `./${resultsRoute}?${queryStrings}`;
      navigate(url);
    } catch (error) {
      console.error(error);
    }
  };

  _handleFavoriteClick = (e, practiceIdentifier) => {
    e.stopPropagation();
    Events.emit(PubSubEvents.toggleFavorite, practiceIdentifier);
  };

  _openTabliteWidget = (e) => {
    e.stopPropagation();
    const { selectedPractice } = this.state;
    const { navigate, location, config } = this.props;
    const { search } = location;
    const TabliteBookingRoute = generateRoute(config, Routes.tabliteBooking);
    const resultsRoute = generateRoute(config, Routes.results);
    const practiceStoreNum = selectedPractice.practiceRecordId.slice(-4);
    const { name, identifier } = selectedPractice;

    navigate(
      `/${TabliteBookingRoute}?storeNumber=${practiceStoreNum}&storeBrand=${name}&practiceId=${identifier}`,
      {
        preventScrollReset: true,
        state: { prevPage: `${resultsRoute}${search}` },
      }
    );
  };

  _onShowTabliteBookingCta = () => {
    // Options to show the tablite CTA
    const { t, config } = this.props;
    const { enable_demo_appointment } = config;
    const { isWholesale, tabliteBooking } = this.state.selectedPractice;
    const shouldShowTabliteBooking =
      !isWholesale &&
      enable_demo_appointment &&
      tabliteBooking?.toLowerCase() === 'on';

    return (
      shouldShowTabliteBooking && (
        <span
          className="map-info-content__link--booking-url"
          onClick={this._openTabliteWidget}
        >
          {t('text_book_in_store_services', 'Book in-store services')}
        </span>
      )
    );
  };

  _onSelectMarker = (practice) => {
    if (practice) {
      const { practiceId } = practice;
      const { markers, lastMarker } = this.state;
      const markerToSelect = markers.find((p) => p.practiceId === practiceId);

      if (markerToSelect) {
        const { t, config, location } = this.props;
        const {
          name,
          phone,
          address1,
          address2,
          city,
          region,
          badgeHtmls,
          offers,
          bookingForm,
          appointmentUrl,
          schedule,
          isWholesale,
          isFavorite,
          badges
        } = practice;

        const {
          enable_rtl_orientation,
          use_transitions_theme,
          hide_city_province,
          enable_favorites,
          show_expert_badges,
          enable_eexpert_badge_to_offer,
          disable_retail_booking
        } = config;

        const filteredBadge = removeNullValues(badgeHtmls);
        const hasBadge = filteredBadge?.length > 0;
        const hasOffers = offers?.length > 0;
        const offer = offers[0]?.toString();
        const dateTime = new DateTime();
        const weekday = DateTime.weekdays()[dateTime.getDay()];
        const todayHours = getPracticeCloseHours(config, weekday, practice);
        const formattedPhone = getPracticeFormattedPhoneNumber(config, phone);
        const textMapping = APPLICATION_CONSTANTS.GLOBAL.EXPERT_TEXT_MAP;
        const translatedBadge = textMapping[badges[0]];
        let todayHoursText = '';

        const { search } = location;
        const queryParams = new URLSearchParams(search);
        const urlUserId = queryParams.get('userId');
        const showHeartButton = enable_favorites && (window.locatorTenancy.userId || urlUserId);
        const heartImgUrl = enable_favorites
          ? `${window.locatorTenancy.locatorRootUri}/assets/images/heart-${
              isFavorite ? 'filled' : 'unfilled'
            }.png`
          : null;

        const cityRegion = hide_city_province ? '' : `, ${city} ${region}`;

        const fullAddress = `${address1}${
          address2 ? ` ${address2}` : ''
        }${cityRegion}`;

        if (todayHours) {
          todayHoursText = `${t('text_open_until')} ${todayHours}`;
        } else {
          todayHoursText = t('text_closed');
        }

        if (!this._mapInfoContentCont) {
          this._mapInfoContentCont = document.createElement('div');
        }

        const disabledBookingForRetailStores = !isWholesale && disable_retail_booking;
        const hasBookButton =
          config.show_book_now && !disabledBookingForRetailStores && (appointmentUrl || bookingForm);

        const bookingText = () => {
          let bookingTranslation = t('text_book_now');

          if (bookingForm === BookingFormOptions.salesforceForm) {
            bookingTranslation = t('text_booking_request_appointment');
          }

          if (isWholesale) {
            bookingTranslation = t('text_book_exam');
          }

          return bookingTranslation;
        };

        ReactDOM.render(
          <MapInfoContent>
            <div
              className={`map-info-content__content ${
                show_expert_badges && hasOffers
                  ? 'map-info-content__content--has-offers'
                  : ''
              }`}
            >
              {show_expert_badges && hasOffers && (
                <div
                  className="map-info-content__offer"
                  dangerouslySetInnerHTML={{ __html: offer }}
                />
              )} 
              {
                checkIsEexpertBadge(filteredBadge?.[0], enable_eexpert_badge_to_offer) && hasBadge ? (
                  <div className="practice-card-offer" dangerouslySetInnerHTML={{ __html: translatedBadge }} />
                ) : (
                  show_expert_badges && hasBadge && (
                    <div
                      className="map-info-content__promo"
                      dangerouslySetInnerHTML={{ __html: filteredBadge?.[0] }}
                    ></div>
                  )
                )
              }
              <div className="map-info-content__details">
                <div className="map-info-content__practice-wrapper">
                  <h3
                    className={`map-info-content__practice ${
                      !hasBadge ? 'map-info-content__address--no-badge' : ''
                    }`}
                  >
                    {name}
                  </h3>
                  {showHeartButton && (
                    <img
                      width={16}
                      height={16}
                      alt={`heart-${isFavorite ? 'filled' : 'unfilled'}`}
                      src={heartImgUrl}
                      className={`map-info-content__practice-favorite${
                        isFavorite ? '--filled' : ''
                      }`}
                      onClick={(e) =>
                        this._handleFavoriteClick(e, practice.identifier)
                      }
                    />
                  )}
                </div>
                <address
                  className={getClassNameOrientation(
                    'map-info-content__address',
                    enable_rtl_orientation
                  )}
                >
                  {fullAddress}
                </address>
                {schedule && (
                  <p className="map-info-content__hours">{todayHoursText}</p>
                )}
                <div className="map-info-content__cta">
                  {phone && (
                    <a
                      data-element-id={AdobeOnClickEventId.callNow}
                      data-description={AdobeOnClickEventId.callNow}
                      className="map-info-content__link"
                      href={`tel:${phone}`}
                      target="_parent"
                      onClick={(e) => this._onPhoneClick(practice, e)}
                    >
                      {use_transitions_theme
                        ? t('text_call_now')
                        : formattedPhone}
                    </a>
                  )}
                  <div className="map-info-content__cta-link-containter">
                    {hasBookButton && (
                      <span
                        data-element-id={AdobeOnClickEventId.bookNow}
                        data-description={AdobeOnClickEventId.bookNow}
                        className="map-info-content__link--booking-url"
                        onClick={(e) => this._onBookNowClick(practice, e)}
                      >
                        {bookingText()}
                      </span>
                    )}
                    {this._onShowTabliteBookingCta()}
                  </div>
                </div>
              </div>
            </div>
          </MapInfoContent>,
          this._mapInfoContentCont
        );

        trackEvent(GaAnalyticEvents.map.pinClick(practice));
        trackEvent(GaAnalyticEvents.map.practiceClick(practice));
        trackEvent(GaAnalyticEvents.map.practiceClickApi(practice));

        if (config.map_pin_image_selected || config.map_pin_icon_selected) {
          if (lastMarker) {
            lastMarker.setIcon(markerToSelect.icon);
            lastMarker.setZIndex(0);
          }

          if (config.map_pin_image_selected) {
            markerToSelect.setIcon(
              `${window.locatorTenancy.locatorRootUri}${config.map_pin_image_selected}`
            );
          } else {
            markerToSelect.setIcon({
              ...markerToSelect.icon,
              ...config.map_pin_icon_selected,
            });
          }
          
          markerToSelect.setZIndex(9999);
        }

        this._mapInfoWindow.setContent(this._mapInfoContentCont);
        this._mapInfoWindow.open(this._map, markerToSelect);
        this._map.panTo(markerToSelect.position);
        this.setState(() => ({ lastMarker: markerToSelect }));
      }
    }
  };

  _onWindowResize = () => {
    if (this._currentMapBounds) {
      this._map.fitBounds(this._currentMapBounds);
    }
  };

  /**
   * Renders the component.
   */
  render() {
    return <div className="map" ref={this._mapRef}></div>;
  }
}

Map.propTypes = {
  practices: PropTypes.array,
  t: PropTypes.func.isRequired,
  config: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  searchLocationCoords: PropTypes.object.isRequired,
  isFirstLoad: PropTypes.bool,
};

Map.defaultProps = {
  practices: [],
};

export default withConfig(withRouter(withTranslation()(Map)));
